// Generated from legacy db.ts to keep files under 500 lines.
import { sql } from "../../base";
import type { Matter } from "../../types";

const mattersModule = {
  async findByUserId(userId: string, includeArchived: boolean = false): Promise<Matter[]> {
    try {
      const result = includeArchived 
        ? await sql`
            SELECT * FROM matters
            WHERE user_id = ${userId}
            ORDER BY position ASC, created_at DESC
          `
        : await sql`
            SELECT * FROM matters
            WHERE user_id = ${userId} AND (is_archived = false OR is_archived IS NULL)
            ORDER BY position ASC, created_at DESC
          `;
      return (result || []) as Matter[];
    } catch (error) {
      console.error('Error fetching matters:', error);
      return [];
    }
  },
  
  async findById(id: string, userId: string): Promise<Matter | null> {
    try {
      const result = await sql`
        SELECT * FROM matters
        WHERE id = ${id} AND user_id = ${userId}
        LIMIT 1
      `;
      return result[0] as Matter || null;
    } catch (error) {
      console.error('Error fetching matter:', error);
      return null;
    }
  },
  
  async create(data: Omit<Matter, 'id' | 'created_at' | 'updated_at'>): Promise<Matter> {
    const result = await sql`
      INSERT INTO matters (user_id, title, description, status, position, project_id, client_id, type_id, start_date, deadline)
      VALUES (${data.user_id}, ${data.title}, ${data.description}, 
              ${data.status || 'Active'}, ${data.position}, ${data.project_id}, ${data.client_id}, ${data.type_id},
              ${data.start_date}, ${data.deadline})
      RETURNING *
    `;
    return result[0] as Matter;
  },
  
  async update(id: string, userId: string, data: Partial<Matter>): Promise<Matter> {
    const result = await sql`
      UPDATE matters
      SET title = COALESCE(${data.title}, title),
          description = COALESCE(${data.description}, description),
          status = COALESCE(${data.status}, status),
          position = COALESCE(${data.position}, position),
          project_id = CASE WHEN ${data.project_id === undefined} THEN project_id ELSE ${data.project_id} END,
          client_id = CASE WHEN ${data.client_id === undefined} THEN client_id ELSE ${data.client_id} END,
          type_id = CASE WHEN ${data.type_id === undefined} THEN type_id ELSE ${data.type_id} END,
          start_date = CASE WHEN ${data.start_date === undefined} THEN start_date ELSE ${data.start_date} END,
          deadline = CASE WHEN ${data.deadline === undefined} THEN deadline ELSE ${data.deadline} END,
          updated_at = now()
      WHERE id = ${id} AND user_id = ${userId}
      RETURNING *
    `;
    return result[0] as Matter;
  },
  
  async canArchive(id: string, userId: string): Promise<boolean> {
    try {
      const result = await sql`
        SELECT can_archive_matter(${id}) as can_archive
      `;
      return result[0]?.can_archive || false;
    } catch (error) {
      console.error('Error checking if matter can be archived:', error);
      return false;
    }
  },

  async archive(id: string, userId: string): Promise<boolean> {
    try {
      const result = await sql`
        SELECT archive_matter(${id}, ${userId}) as success
      `;
      return result[0]?.success || false;
    } catch (error) {
      console.error('Error archiving matter:', error);
      return false;
    }
  },

  async unarchive(id: string, userId: string): Promise<boolean> {
    try {
      const result = await sql`
        SELECT unarchive_matter(${id}, ${userId}) as success
      `;
      return result[0]?.success || false;
    } catch (error) {
      console.error('Error unarchiving matter:', error);
      return false;
    }
  },
  
  async markComplete(id: string, userId: string): Promise<Matter> {
    const result = await sql`
      UPDATE matters
      SET is_complete = true,
          completed_at = now(),
          updated_at = now()
      WHERE id = ${id} AND user_id = ${userId}
      RETURNING *
    `;
    return result[0] as Matter;
  },
  
  async markIncomplete(id: string, userId: string): Promise<Matter> {
    const result = await sql`
      UPDATE matters
      SET is_complete = false,
          completed_at = NULL,
          updated_at = now()
      WHERE id = ${id} AND user_id = ${userId}
      RETURNING *
    `;
    return result[0] as Matter;
  },

  async delete(id: string, userId: string): Promise<boolean> {
    const result = await sql`
      DELETE FROM matters
      WHERE id = ${id} AND user_id = ${userId}
    `;
    return result.count > 0;
  }
};

export type MattersModule = typeof mattersModule;

export function registerMatters(db: { matters?: MattersModule }) {
  db.matters = mattersModule;
}

export { mattersModule as matters };
